import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const switchRootVariants = cva(
  "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        subtle:
          "h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        big: "h-6 w-11 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        bigSubtle:
          "h-6 w-11 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const switchThumbVariants = cva(
  "pointer-events-none block rounded-full shadow-lg ring-0 transition-transform",
  {
    variants: {
      variant: {
        default:
          "h-4 w-4 bg-primary data-[state=checked]:bg-white data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        subtle:
          "h-4 w-4 bg-white data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        big: "h-5 w-5 bg-primary data-[state=checked]:bg-white data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
        bigSubtle:
          "h-5 w-5 bg-white data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> &
  VariantProps<typeof switchRootVariants> &
  VariantProps<typeof switchThumbVariants>;

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, variant, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchRootVariants({ variant }), className)}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb className={cn(switchThumbVariants({ variant }))} />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
